import React, { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FetchTable from 'components/table/FetchTable';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { useForm } from 'react-hook-form';
import { Column } from 'react-table';
import { buildFilters } from 'components/table/utils/filtersConfig';
import { useModal } from 'store/modal';
import { formatDate } from 'utils/helpers/formatters';
import { FaTimes } from 'react-icons/fa';
import Form from 'components/form/Form';
import { Form as FormBootstrap } from 'react-bootstrap';
import FormGroup from 'components/form/FormGroup';
import Button from 'components/Button';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { getSubdomain } from 'utils/helpers/routing';
import { getUserInfo } from 'utils/auth';
import NotAuthorizedScreen from 'routes/not-authorized';
import TooltipWrapper from 'components/TooltipWrapper';
import InnerWrapper from '../InnerWrapper';
import Calendar from './calendar';
import { ILeave, IInputs } from './types';

const DayOff: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { showModal, hideModal } = useModal();

  const currentDate = new Date();

  const [dropdowns, setDropdowns] = useState<any>({});
  const [reloadTable, setReloadTable] = useState(false);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(currentDate);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidaysCache, setHolidaysCache] = useState<{ [year: number]: string[] }>({});
  const [dayOffType, setDayOffType] = useState<'Full Day' | 'Hourly'>('Full Day');
  const [isRestInTwoDays, setIsRestInTwoDays] = useState(false);
  const [showAddOvertimeModal, setShowAddOvertimeModal] = useState(false);
  const [isModalTrigger, setIsModalTrigger] = useState(false);
  const [isAddingOvertime, setIsAddingOvertime] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const isForm76Active = getUserInfo().featureForm76;
  const isOperator = getSubdomain() === 'operators';

  const handleRestInTwoDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsRestInTwoDays(event.target.checked);
  };

  const methods = useForm<IInputs>({
    defaultValues: {
      work_at_date: new Date(),
      type: { value: 'Full Day', label: t('structure.overtime.fullDay') },
    },
  });

  useEffect(() => {
    const fetchDropdowns = async () => {
      setIsLoadingDropdowns(true);
      try {
        const response = await API.get('overtimes/dropdown-options');
        const usersResponse = await API.get('/users');
        const userOptions = usersResponse.data.items.map((user: any) => ({
          value: user.id,
          label: `${user.first_name} ${user.last_name}`,
        }));

        setDropdowns({
          ...response.data,
          userOptions,
        });

        setIsLoadingDropdowns(false);
      } catch (error) {
        setIsLoadingDropdowns(false);
      }
    };

    fetchDropdowns();
  }, []);

  useEffect(() => {
    // Fetch the holidays for the selected year
    const fetchHolidays = async () => {
      // If holidays for the year are already fetched, skip fetching
      if (holidaysCache[selectedYear]) {
        return;
      }

      try {
        const response = await API.get(`holidays/${selectedYear}`);
        const holidayDates = response.data.items.map((item: any) => item.date);
        // Update the cache with the newly fetched holidays
        setHolidaysCache(prevCache => ({
          ...prevCache,
          [selectedYear]: holidayDates,
        }));
      } catch (error) {
        showModal({
          type: 'error',
          content: createErrorFromResponse(error).message,
        });
      }
    };

    fetchHolidays();
  }, [selectedYear, holidaysCache]);

  const isHoliday = (date: Date, revert = false) => {
    const day = date.getDay();
    const formattedDate = formatDate(date, 'YYY-mm-dd');
    const holidaysForYear = holidaysCache[date.getFullYear()] || [];
    const isWeekendOrHoliday = day === 0 || day === 6 || holidaysForYear.includes(formattedDate);

    // If revert is true, only allow selection of weekends and holidays
    // Otherwise, allow all days except weekends and holidays
    return revert ? isWeekendOrHoliday : !isWeekendOrHoliday;
  };

  const handleDelete = (leaveId: number) => {
    API.delete(`/overtimes/${leaveId}`)
      .then(response => {
        showModal({
          type: 'success',
          content: t('structure.overtime.rejectedOvertimeSuccessMsg'),
        });
        setReloadTable(prevState => !prevState);
      })
      .catch(error => {
        showModal({
          type: 'error',
          content: createErrorFromResponse(error).message,
        });
      });
  };

  const setTodaysDay = (date: Date) => {
    setSelectedDate(prevDate => new Date(date));
  };

  const formatDateToYYYYMMDD = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
  };

  const handleSubmitForm = (data: IInputs) => {
    const userID = typeof data.user_id === 'string' ? data.user_id : data.user_id.value;

    interface IRequestData {
      user_id: string;
      work_at_date?: string;
      rest_at_date?: string;
      type: string;
      working_hours?: number;
      hours_of_rest_per_day?: number;
      second_rest_at_date?: string;
    }

    let leaveType =
      (data.type as unknown as { value: string }).value === 'Full Day' ? 'single_day_rest' : 'hourly_rest';

    if (isRestInTwoDays) {
      leaveType = 'split_rest';
    }

    const workAtDate = formatDateToYYYYMMDD(data.work_at_date ? data.work_at_date : new Date());
    const restAtDate = formatDateToYYYYMMDD(data.rest_at_date ? data.rest_at_date : new Date());
    const secondRestDate = formatDateToYYYYMMDD(data.second_rest_at_date ? data.second_rest_at_date : new Date());

    const requestData: IRequestData = {
      user_id: userID,
      type: leaveType,
    };

    if (leaveType === 'single_day_rest') {
      requestData.work_at_date = workAtDate;
      requestData.rest_at_date = restAtDate;
    }

    if (leaveType === 'hourly_rest') {
      requestData.work_at_date = workAtDate;
      requestData.rest_at_date = restAtDate;
      requestData.hours_of_rest_per_day = data.hours_of_rest_per_day;
    }

    if (leaveType === 'split_rest') {
      requestData.work_at_date = workAtDate;
      requestData.rest_at_date = restAtDate;
      requestData.second_rest_at_date = secondRestDate;
    }

    if (data.working_hours) {
      requestData.working_hours = data.working_hours;
    }

    API.post('/overtimes/create', requestData)
      .then(response => {
        handleCloseModal();
        showModal({
          type: 'success',
          content: t('structure.overtime.addSuccessMsg'),
        });
        setReloadTable(prevState => !prevState);
      })
      .catch(error => {
        handleCloseModal();
        showModal({
          type: 'error',
          content: createErrorFromResponse(error).message,
        });
      })
      .finally(() => {
        setIsUploading(false);
        setShowAddOvertimeModal(false);
        setIsModalTrigger(prevState => !prevState);
      });
  };

  const confirmReject = (leaveId: number) => {
    showModal({
      type: 'form',
      heading: t('structure.overtime.confirmDocHeading'),
      content: (
        <>
          <p>{t('structure.overtime.confirmRejectDocMsg')}</p>
          <hr />
          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              btnType="danger"
              onClick={() => {
                handleDelete(leaveId);
                hideModal();
              }}
            >
              {t('structure.overtime.confirmRejectDocLabel')}
            </Button>
            <Button
              btnType="light"
              onClick={hideModal}
              outlined
            >
              {t('structure.overtime.cancel')}
            </Button>
          </div>
        </>
      ),
    });
  };

  const handleCloseModal = () => {
    hideModal();
    setShowAddOvertimeModal(false);
  };

  useEffect(() => {
    setSelectedDate(new Date());
    setSelectedYear(new Date().getFullYear());
    setIsRestInTwoDays(false);
    setDayOffType('Full Day');
    setIsRestInTwoDays(false);
  }, [isModalTrigger]);

  useEffect(() => {
    const userOptions = dropdowns.userOptions
      ? dropdowns.userOptions.map((user: any) => ({
          value: user.value,
          label: user.label,
        }))
      : [];

    if (showAddOvertimeModal) {
      const getModalContent = () => (
        <Form
          methods={methods}
          onSubmit={(data: any) => {
            setIsUploading(true);
            handleSubmitForm(data);
          }}
          isUploading={isUploading}
          btnLabel={t('structure.overtime.submit')}
          onCancel={handleCloseModal}
        >
          <FormGroup
            controlId="formGroupWorkAtDate"
            label={t('structure.overtime.workAtDate')}
            type="datepicker"
            name="work_at_date"
            placeholder="01/01/2024"
            dateFormat="dd/MM/yyyy"
            onChange={dateNow => {
              setSelectedYear(dateNow.getFullYear());
            }}
            filterDate={dateNow => isHoliday(dateNow, true)}
            isClearable
            isRequired
          />
          <FormGroup
            controlId="formGroupUserId"
            label={t('structure.overtime.employee')}
            type="select"
            name="user_id"
            isRequired
            isClearable
            isSearchable
            selectOptions={userOptions}
          />
          <FormGroup
            controlId="formGroupDayOffType"
            label={t('structure.overtime.type')}
            type="select"
            name="type"
            selectOptions={[
              { value: 'Full Day', label: t('structure.overtime.fullDay') },
              { value: 'Hourly', label: t('structure.overtime.hourly') },
            ]}
            onChange={e => {
              setDayOffType(e.value);
              setIsRestInTwoDays(false);
            }}
            isRequired
          >
            {dayOffType === 'Full Day' && (
              <div className="d-flex align-items-center ml-3">
                <div className="mr-2">{t('structure.overtime.restInTwoDays')}</div>
                <FormBootstrap.Check
                  custom
                  type="checkbox"
                  id="rest_in_two_days"
                  onChange={e => handleRestInTwoDaysChange(e)}
                />
              </div>
            )}
          </FormGroup>
          {dayOffType === 'Hourly' && (
            <FormGroup
              controlId="formGroupRestAtDate"
              label={t('structure.overtime.startRestingAt')}
              type="datepicker"
              name="rest_at_date"
              placeholder="01/01/2024"
              dateFormat="dd/MM/yyyy"
              onChange={dateNow => {
                setSelectedYear(dateNow.getFullYear());
              }}
              filterDate={isHoliday}
              isClearable
              isRequired={dayOffType === 'Hourly'}
            />
          )}
          {dayOffType === 'Full Day' && (
            <FormGroup
              controlId="formGroupRestAtDate"
              label={t('structure.overtime.restAtDate')}
              type="datepicker"
              name="rest_at_date"
              placeholder="01/01/2024"
              dateFormat="dd/MM/yyyy"
              onChange={dateNow => {
                setSelectedYear(dateNow.getFullYear());
              }}
              filterDate={isHoliday}
              isClearable
              isRequired={dayOffType === 'Full Day'}
            />
          )}
          {dayOffType === 'Full Day' && isRestInTwoDays && (
            <FormGroup
              controlId="formGroupRestAtDate"
              label={t('structure.overtime.restAtDate')}
              type="datepicker"
              name="second_rest_at_date"
              placeholder="01/01/2024"
              dateFormat="dd/MM/yyyy"
              onChange={dateNow => {
                setSelectedYear(dateNow.getFullYear());
              }}
              filterDate={isHoliday}
              isClearable
              isRequired={dayOffType === 'Full Day' && isRestInTwoDays}
            />
          )}
          <FormGroup
            controlId="formGroupWorkingHours"
            label={t('structure.overtime.workingHours')}
            type="number"
            name="working_hours"
            isClearable
          />
          {dayOffType === 'Hourly' && (
            <FormGroup
              controlId="formGroupRestPerDay"
              label={t('structure.overtime.hoursOfRestPerDay')}
              type="number"
              name="hours_of_rest_per_day"
              isClearable
              isRequired={dayOffType === 'Hourly'}
            />
          )}
        </Form>
      );

      showModal({
        type: 'form',
        heading: t('structure.overtime.overtimeHeading'),
        content: getModalContent(),
      });
    }
  }, [showAddOvertimeModal, dayOffType, isRestInTwoDays, isModalTrigger, dropdowns, isUploading]);

  const addOvertime = (date?: Date) => {
    setTimeout(() => setIsAddingOvertime(false), 200);

    setShowAddOvertimeModal(true);
    setIsModalTrigger(prevState => !prevState);

    methods.reset({
      work_at_date: date ? new Date(date) : undefined,
      type: { value: 'Full Day', label: t('structure.overtime.fullDay') },
    });
  };

  const getFirstDateOfMonth = (date: Date): string => {
    const firstDate = new Date(date.getFullYear(), date.getMonth(), 1);
    return formatDateToYYYYMMDD(firstDate);
  };

  const getLastDateOfMonth = (date: Date): string => {
    const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0); // 0 will give the last day of the previous month
    return formatDateToYYYYMMDD(lastDate);
  };

  const tableFilters = useMemo(() => {
    const workDate = buildFilters({
      name: 'work_at_date',
      placeholder: t('structure.overtime.day_work'),
      type: 'alldatepicker',
    });
    const restDate = buildFilters({
      name: 'rest_at_date',
      placeholder: t('structure.overtime.day_off'),
      type: 'alldatepicker',
    });
    const user = buildFilters({
      name: 'user_id',
      placeholder: t('structure.overtime.employee'),
      options: dropdowns.bg?.user || [],
      isMulti: true,
      showTotal: true,
      allowSelectAll: true,
    });
    const group = buildFilters({
      name: 'group_id',
      placeholder: t('structure.overtime.group'),
      options: dropdowns.bg?.group || [],
      isMulti: true,
      showTotal: true,
      allowSelectAll: true,
    });
    const dateFrom = buildFilters({
      name: 'date_from',
      options: [
        {
          value: getFirstDateOfMonth(selectedDate),
          label: 'date_from',
          selected: true,
        },
      ],
      hidden: true,
    });
    const dateTo = buildFilters({
      name: 'date_to',
      options: [
        {
          value: getLastDateOfMonth(selectedDate),
          label: 'date_to',
          selected: true,
        },
      ],
      hidden: true,
    });

    return [workDate, restDate, user, group, dateFrom, dateTo];
  }, [dropdowns, t, selectedDate]);

  const baseColumns: Array<Column<ILeave>> = [
    {
      Header: t('structure.overtime.employee') || '',
      accessor: 'user_name',
    },
    {
      Header: t('structure.overtime.group') || '',
      accessor: 'group_names',
      Cell: ({ value, row }: { value: any; row: any }) => {
        const fullGroups = value.join(', ');
        const groupsPreview = fullGroups.length > 20 ? `${fullGroups.substr(0, 20)}...` : fullGroups;

        return (
          <TooltipWrapper
            key={row.index}
            id="tooltip-groups"
            overlay={`${fullGroups}`}
          >
            {groupsPreview}
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('structure.overtime.day_work') || '',
      accessor: 'work_at_date',
      Cell: ({ row }: any) => (
        <>{`${formatDate(new Date(row.original.work_at_date), 'dd.mm.YYY')} ${t('leaves.yearShort')}`}</>
      ),
    },
    {
      Header: t('structure.overtime.day_off') || '',
      accessor: 'rest_at_date',
      Cell: ({ row }: any) => {
        const isSplitRest = row.original.type === 'split_rest';
        const isHourlyRest = row.original.type === 'hourly_rest';

        if (isSplitRest) {
          return (
            <>{`${formatDate(new Date(row.original.rest_at_date), 'dd.mm.YYY')} ${t('leaves.yearShort')} ${t(
              'common.and'
            )} ${formatDate(new Date(row.original.second_rest_at_date), 'dd.mm.YYY')} ${t('leaves.yearShort')}`}</>
          );
        }

        if (isHourlyRest) {
          return (
            <>{`${t('common.from')} ${formatDate(new Date(row.original.rest_at_date), 'dd.mm.YYY')} ${t(
              'leaves.yearShort'
            )} ${t('common.for')} ${row.original.hours_of_rest_per_day}${t('structure.overtime.hoursSmall')}`}</>
          );
        }

        return <>{`${formatDate(new Date(row.original.rest_at_date), 'dd.mm.YYY')} ${t('leaves.yearShort')}`}</>;
      },
    },
    {
      Header: t('structure.overtime.workingHours') || '',
      accessor: 'working_hours',
      width: 50,
      Cell: ({ row }: any) => (
        <div>
          {row.original.working_hours > 0
            ? `${Math.trunc(row.original.working_hours)}${t('structure.overtime.hoursSmall')}`
            : '-'}
        </div>
      ),
    },
    {
      Header: t('structure.overtime.actions') || '',
      accessor: 'actions',
      disableSortBy: true,
      width: 50,
      Cell: ({ row }: any) => (
        <TooltipWrapper
          id="tooltip-trash"
          overlay={t('common.delete')}
        >
          <Button
            btnType="danger"
            onClick={() => confirmReject(row.original.id)}
          >
            <FaTimes />
          </Button>
        </TooltipWrapper>
      ),
    },
  ];

  const columns = isOperator ? baseColumns.filter(col => col.accessor !== 'actions') : baseColumns;

  if (isForm76Active || (isOperator && client?.is_form_76)) {
    return (
      <InnerWrapper isLoading={isLoadingDropdowns}>
        <div
          className="d-flex flex-column align-items-center"
          style={{ gap: 20 }}
        >
          <div
            className="d-flex flex-column flex-md-row justify-content-md-center justify-content-between w-100"
            style={{ gap: 50 }}
          >
            <div
              className="d-flex flex-column align-items-center"
              style={{ gap: 20 }}
            >
              {!isOperator && (
                <Button
                  className="mt-3"
                  type="button"
                  onClick={() => {
                    setIsAddingOvertime(true);
                    addOvertime();
                  }}
                  disabled={isAddingOvertime}
                >
                  {t('structure.overtime.overtimeHeading')}
                </Button>
              )}
              <Calendar
                onDateChange={setTodaysDay}
                onAddOvertime={addOvertime}
              />
            </div>
            <div>
              <FetchTable<ILeave>
                entityEndpoint="overtimes"
                columns={columns}
                entityName={buildClientEntity('leaves', client)}
                filters={tableFilters}
                hideSearch
                pagination
                pageLimit={10}
                reloadKey={reloadTable}
              />
            </div>
          </div>
        </div>
      </InnerWrapper>
    );
  }

  return <NotAuthorizedScreen />;
};

export default DayOff;
